/**
 * KT-CRM affiliate attribution for Shopify storefronts.
 * Captures ?ref= and UTMs into cookie kt_crm_attribution, syncs to cart attributes.
 * Contract: docs/affiliate-system.md
 */
(function (global) {
  "use strict";

  var COOKIE_NAME = "kt_crm_attribution";
  var COOKIE_MAX_AGE_DAYS = 90;
  var CART_SYNC_DEBOUNCE_MS = 400;

  var DEFAULT_LEGACY_PARAMS = {
    gfp: "goaffiliatepro",
    gfp_ref: "goaffiliatepro",
    aff: "solid_affiliate",
    affiliate_id: "solid_affiliate",
  };

  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  var CLICK_ID_KEYS = ["gclid", "fbclid", "msclkid"];
  var ACCELERATED_CHECKOUT_SELECTOR =
    "shopify-accelerated-checkout, .shopify-payment-button, [data-shopify=\"payment-button\"], .shopify-payment-button__button";
  var ACCELERATED_HOST_TAG = "shopify-accelerated-checkout";
  var INTERCEPT_DEDUPE_MS = 600;
  var DEBUG_DEFAULT = false;
  var WALLET_SELECTORS =
    "shopify-pay-button, .paypal-button, .paypal-buttons, [data-testid=\"PayPalInContext-button\"], " +
    "[class*=\"wallet-button\"], .shopify-payment-button__more-options";
  var CART_DRAWER_SELECTORS =
    "cart-drawer, [data-cart-drawer], #CartDrawer, .cart-drawer";
  var DAWN_PRODUCT_BUTTONS = ".product-form__buttons";
  var DAWN_ADD_TO_CART = 'button[name="add"], .product-form__submit';
  var WALLET_UI_SELECTORS =
    '[data-shopify="payment-button"], .shopify-payment-button, .shopify-payment-button__more-options';
  var REPLACEMENT_SELECTOR = '[data-kt-crm-replacement="1"]';

  var debugState = {
    step: "boot",
    variantId: null,
    variantSource: null,
    lastIntercept: null,
    lastError: null,
  };
  var interceptGuard = { until: 0, inFlight: false };
  var debugOverlayEl = null;
  var overrideObserver = null;
  var overrideApplyTimer = null;
  var overrideApplyInProgress = false;
  var overrideRetryTimer = null;

  function trim(v) {
    return v == null ? "" : String(v).trim();
  }

  function lowerUtm(v) {
    var s = trim(v);
    return s ? s.toLowerCase() : "";
  }

  function getConfig() {
    var cfg = global.KT_CRM_AFFILIATE || {};
    return {
      legacyParams: Object.assign({}, DEFAULT_LEGACY_PARAMS, cfg.legacyParams || {}),
      debug: cfg.debug != null ? Boolean(cfg.debug) : DEBUG_DEFAULT,
      debugOverlay: cfg.debugOverlay != null ? Boolean(cfg.debugOverlay) : DEBUG_DEFAULT,
      interceptAcceleratedCheckout: cfg.interceptAcceleratedCheckout !== false,
      customCheckoutButton: cfg.customCheckoutButton !== false,
      blockNativeAccelerated: cfg.blockNativeAccelerated !== false,
      checkoutButtonLabel: trim(cfg.checkoutButtonLabel) || null,
      cookieName: trim(cfg.cookieName) || COOKIE_NAME,
      cookieMaxAgeDays: cfg.cookieMaxAgeDays || COOKIE_MAX_AGE_DAYS,
    };
  }

  function log(cfg) {
    if (!cfg.debug || !global.console) return;
    global.console.log.apply(global.console, ["[kt-crm-attribution]"].concat(Array.prototype.slice.call(arguments, 1)));
  }

  function setDebugStep(cfg, step, extra) {
    debugState.step = step;
    if (extra && typeof extra === "object") Object.assign(debugState, extra);
    updateDebugOverlay(cfg);
  }

  function pathTags(ev) {
    var path = ev && ev.composedPath ? ev.composedPath() : [];
    var tags = [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.tagName) tags.push(String(node.tagName).toUpperCase());
    }
    return tags;
  }

  function findAcceleratedHostInEvent(ev) {
    var path = ev && ev.composedPath ? ev.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || !node.tagName) continue;
      var tag = String(node.tagName).toLowerCase();
      if (tag === ACCELERATED_HOST_TAG) return node;
      if (node.matches && node.matches(ACCELERATED_CHECKOUT_SELECTOR)) return node;
    }
    var el = ev && ev.target;
    if (el && el.closest) return el.closest(ACCELERATED_CHECKOUT_SELECTOR);
    return null;
  }

  function getAcceleratedHostsSnapshot() {
    if (!global.document) return [];
    var nodes = global.document.querySelectorAll(ACCELERATED_HOST_TAG);
    var hosts = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      hosts.push({
        tag: el.tagName,
        recommended: el.getAttribute("recommended"),
        variantParams: el.getAttribute("variant-params"),
        inDom: true,
      });
    }
    return hosts;
  }

  function scanAcceleratedHosts(cfg) {
    var hosts = getAcceleratedHostsSnapshot();
    log(cfg, "found " + hosts.length + " shopify-accelerated-checkout host(s)", hosts);
    return hosts;
  }

  function updateDebugOverlay(cfg) {
    cfg = cfg || getConfig();
    if (!cfg.debug || !cfg.debugOverlay || !global.document) {
      if (debugOverlayEl && debugOverlayEl.parentNode) debugOverlayEl.parentNode.removeChild(debugOverlayEl);
      debugOverlayEl = null;
      return;
    }
    if (!debugOverlayEl) {
      debugOverlayEl = global.document.createElement("div");
      debugOverlayEl.className = "kt-crm-debug-hud";
      debugOverlayEl.setAttribute(
        "style",
        "position:fixed;bottom:12px;right:12px;z-index:99999;background:#111;color:#0f0;" +
          "font:12px/1.4 monospace;padding:10px 12px;border-radius:6px;max-width:320px;" +
          "opacity:0.92;pointer-events:none;",
      );
      global.document.body.appendChild(debugOverlayEl);
    }
    var attr = getAttribution();
    var ref = attr && attr.affiliate_ref ? attr.affiliate_ref : "—";
    var err = debugState.lastError ? String(debugState.lastError) : "—";
    debugOverlayEl.textContent =
      "kt-crm debug\nstep: " +
      debugState.step +
      "\nref: " +
      ref +
      "\nvariant: " +
      (debugState.variantId || "—") +
      " (" +
      (debugState.variantSource || "—") +
      ")\nerror: " +
      err;
  }

  function parseVariantParams(raw) {
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        for (var i = 0; i < parsed.length; i++) {
          if (parsed[i] && parsed[i].id) return String(parsed[i].id);
        }
      }
    } catch (_e) {
      /* ignore */
    }
    return null;
  }

  function resolveVariantId(form, host, cfg) {
    cfg = cfg || getConfig();
    var attempts = [];

    function ok(source, id) {
      id = trim(id);
      if (!id || id === "null" || id === "undefined") return null;
      debugState.variantId = id;
      debugState.variantSource = source;
      log(cfg, "variant resolved:", id, "via", source);
      return id;
    }

    if (form) {
      var input = form.querySelector('[name="id"]');
      if (input && trim(input.value)) {
        var v = ok("form-input", input.value);
        if (v) return v;
      }
      attempts.push("form-input empty");
      try {
        var fd = new FormData(form);
        var fdId = fd.get("id");
        if (fdId) {
          v = ok("form-data", fdId);
          if (v) return v;
        }
        attempts.push("form-data empty");
      } catch (_e) {
        attempts.push("form-data error");
      }
    } else {
      attempts.push("no form");
    }

    if (host) {
      var recommended = host.getAttribute("recommended");
      if (recommended && recommended !== "null") {
        v = ok("recommended", recommended);
        if (v) return v;
      }
      attempts.push("recommended=" + (recommended || "null"));
      var fromParams = parseVariantParams(host.getAttribute("variant-params"));
      if (fromParams) {
        v = ok("variant-params", fromParams);
        if (v) return v;
      }
      attempts.push("variant-params empty");
    } else {
      attempts.push("no host");
    }

    if (global.document) {
      var dataEl = global.document.querySelector("[data-selected-variant], [data-variant-id]");
      if (dataEl) {
        var dv = dataEl.getAttribute("data-selected-variant") || dataEl.getAttribute("data-variant-id");
        if (dv) {
          v = ok("data-attribute", dv);
          if (v) return v;
        }
      }
      attempts.push("data-attribute empty");
      var pf = global.document.querySelector("product-form");
      if (pf && pf.querySelector) {
        var pfInput = pf.querySelector('[name="id"]');
        if (pfInput && trim(pfInput.value)) {
          v = ok("product-form", pfInput.value);
          if (v) return v;
        }
      }
      attempts.push("product-form empty");
    }

    debugState.variantId = null;
    debugState.variantSource = null;
    log(cfg, "variant resolve FAILED", attempts);
    return null;
  }

  function readCookie(name) {
    if (!global.document || !global.document.cookie) return null;
    var prefix = name + "=";
    var parts = global.document.cookie.split(";");
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+/, "");
      if (part.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(part.slice(prefix.length));
        } catch (_e) {
          return part.slice(prefix.length);
        }
      }
    }
    return null;
  }

  function writeCookie(name, value, maxAgeDays) {
    if (!global.document) return;
    var maxAge = maxAgeDays * 24 * 60 * 60;
    global.document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; path=/; max-age=" +
      maxAge +
      "; SameSite=Lax";
  }

  function parseCookieAttribution(cfg) {
    var raw = readCookie(cfg.cookieName);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch (_e) {
      return null;
    }
  }

  function parseUrlParams(search) {
    var out = {};
    if (!search) return out;
    var q = search.charAt(0) === "?" ? search.slice(1) : search;
    if (!q) return out;
    q.split("&").forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf("=");
      var key = eq >= 0 ? pair.slice(0, eq) : pair;
      var val = eq >= 0 ? pair.slice(eq + 1) : "";
      try {
        key = decodeURIComponent(key.replace(/\+/g, " "));
        val = decodeURIComponent(val.replace(/\+/g, " "));
      } catch (_e) {
        /* keep raw */
      }
      if (key) out[key] = val;
    });
    return out;
  }

  function landingPath() {
    if (!global.location) return null;
    var path = global.location.pathname + global.location.search;
    return path || "/";
  }

  function landingUrl() {
    if (!global.location) return null;
    var href = global.location.href;
    return href && href.length > 500 ? href.slice(0, 500) : href;
  }

  function normalizeAffiliateRef(ref) {
    var s = trim(ref);
    return s ? s.toLowerCase() : "";
  }

  function extractFromParams(params, cfg) {
    var patch = {};
    var affiliateParamRaw = null;
    var newAffiliate = false;

    var ref = trim(params.ref);
    if (ref) {
      patch.affiliate_ref = ref.toLowerCase();
      affiliateParamRaw = "ref=" + ref;
      newAffiliate = true;
    }

    for (var legacyKey in cfg.legacyParams) {
      if (!Object.prototype.hasOwnProperty.call(cfg.legacyParams, legacyKey)) continue;
      var legacyVal = trim(params[legacyKey]);
      if (!legacyVal) continue;
      patch.legacy_source = cfg.legacyParams[legacyKey];
      patch.legacy_affiliate_id = legacyVal;
      if (!affiliateParamRaw) affiliateParamRaw = legacyKey + "=" + legacyVal;
      newAffiliate = true;
    }

    UTM_KEYS.forEach(function (key) {
      var v = lowerUtm(params[key]);
      if (v) patch[key] = v;
    });

    var clickIds = {};
    CLICK_ID_KEYS.forEach(function (key) {
      var v = trim(params[key]);
      if (v) clickIds[key] = v;
    });
    if (Object.keys(clickIds).length) patch.click_ids = clickIds;

    if (affiliateParamRaw) patch.affiliate_param_raw = affiliateParamRaw;

    var salesRepRef = trim(params.sales_rep_ref);
    if (salesRepRef) patch.sales_rep_ref = salesRepRef.toLowerCase();
    if (params.sales_rep_close === "1" || params.sales_rep_close === "true") {
      patch.sales_rep_close = true;
    }

    return { patch: patch, newAffiliate: newAffiliate };
  }

  function mergeAttribution(existing, patch, newAffiliate) {
    var base = existing && typeof existing === "object" ? Object.assign({}, existing) : {};
    var next = Object.assign({}, base);

    if (newAffiliate) {
      if (patch.affiliate_ref) next.affiliate_ref = patch.affiliate_ref;
      if (patch.affiliate_param_raw) next.affiliate_param_raw = patch.affiliate_param_raw;
      if (patch.legacy_source) next.legacy_source = patch.legacy_source;
      if (patch.legacy_affiliate_id) next.legacy_affiliate_id = patch.legacy_affiliate_id;
      else if (patch.affiliate_ref) {
        delete next.legacy_source;
        delete next.legacy_affiliate_id;
      }
    } else {
      if (!next.affiliate_ref && patch.affiliate_ref) next.affiliate_ref = patch.affiliate_ref;
      if (!next.affiliate_param_raw && patch.affiliate_param_raw)
        next.affiliate_param_raw = patch.affiliate_param_raw;
      if (!next.legacy_source && patch.legacy_source) next.legacy_source = patch.legacy_source;
      if (!next.legacy_affiliate_id && patch.legacy_affiliate_id)
        next.legacy_affiliate_id = patch.legacy_affiliate_id;
    }

    UTM_KEYS.forEach(function (key) {
      if (patch[key] && (!next[key] || newAffiliate)) next[key] = patch[key];
      else if (!next[key] && patch[key]) next[key] = patch[key];
    });

    var baseClick =
      next.click_ids && typeof next.click_ids === "object" ? Object.assign({}, next.click_ids) : {};
    var patchClick =
      patch.click_ids && typeof patch.click_ids === "object" ? patch.click_ids : {};
    var mergedClick = Object.assign({}, baseClick);
    for (var ck in patchClick) {
      if (Object.prototype.hasOwnProperty.call(patchClick, ck) && patchClick[ck]) {
        if (!mergedClick[ck] || newAffiliate) mergedClick[ck] = patchClick[ck];
      }
    }
    if (Object.keys(mergedClick).length) next.click_ids = mergedClick;
    else delete next.click_ids;

    if (patch.sales_rep_ref) next.sales_rep_ref = patch.sales_rep_ref;
    if (patch.sales_rep_close) next.sales_rep_close = true;

    if (newAffiliate) {
      next.landing_path = landingPath();
      next.landing_url = landingUrl();
      next.captured_at = new Date().toISOString();
    } else {
      if (!next.landing_path) next.landing_path = landingPath();
      if (!next.landing_url) next.landing_url = landingUrl();
      if (!next.captured_at) next.captured_at = new Date().toISOString();
    }

    return next;
  }

  function hasAttributionData(obj) {
    if (!obj || typeof obj !== "object") return false;
    if (trim(obj.affiliate_ref)) return true;
    if (trim(obj.sales_rep_ref)) return true;
    if (trim(obj.legacy_affiliate_id)) return true;
    for (var i = 0; i < UTM_KEYS.length; i++) {
      if (trim(obj[UTM_KEYS[i]])) return true;
    }
    if (obj.click_ids && typeof obj.click_ids === "object") {
      for (var k in obj.click_ids) {
        if (Object.prototype.hasOwnProperty.call(obj.click_ids, k) && trim(obj.click_ids[k])) return true;
      }
    }
    return false;
  }

  function buildOrderAttributes(attribution) {
    if (!hasAttributionData(attribution)) return null;

    var attrs = {
      kt_crm_attribution: JSON.stringify(attribution),
    };

    if (trim(attribution.affiliate_ref)) attrs.affiliate_ref = trim(attribution.affiliate_ref);
    UTM_KEYS.forEach(function (key) {
      if (trim(attribution[key])) attrs[key] = trim(attribution[key]);
    });
    if (trim(attribution.landing_path)) attrs.landing_path = trim(attribution.landing_path);
    if (trim(attribution.landing_url)) attrs.landing_url = trim(attribution.landing_url);

    var clickIds =
      attribution.click_ids && typeof attribution.click_ids === "object" ? attribution.click_ids : {};
    CLICK_ID_KEYS.forEach(function (key) {
      if (trim(clickIds[key])) attrs[key] = trim(clickIds[key]);
    });

    if (trim(attribution.legacy_source))
      attrs._kt_crm_legacy_affiliate_source = trim(attribution.legacy_source);
    if (trim(attribution.legacy_affiliate_id))
      attrs._kt_crm_legacy_affiliate_id = trim(attribution.legacy_affiliate_id);
    if (trim(attribution.sales_rep_ref)) attrs.sales_rep_ref = trim(attribution.sales_rep_ref);
    if (attribution.sales_rep_close === true || attribution.sales_rep_close === "true") {
      attrs.sales_rep_close = "1";
    }

    return attrs;
  }

  function captureFromUrl(search) {
    var cfg = getConfig();
    var params = parseUrlParams(search != null ? search : global.location && global.location.search);
    var extracted = extractFromParams(params, cfg);
    if (!Object.keys(extracted.patch).length) return null;

    var existing = parseCookieAttribution(cfg);
    var prevRef = normalizeAffiliateRef(existing && existing.affiliate_ref);
    var prevRepRef = normalizeAffiliateRef(existing && existing.sales_rep_ref);
    var merged = mergeAttribution(existing, extracted.patch, extracted.newAffiliate);
    if (!hasAttributionData(merged)) return null;

    var affiliateChanged =
      extracted.newAffiliate && prevRef !== normalizeAffiliateRef(merged.affiliate_ref);
    var salesRepChanged =
      extracted.patch.sales_rep_ref &&
      extracted.patch.sales_rep_close &&
      (prevRepRef !== normalizeAffiliateRef(merged.sales_rep_ref) ||
        !(existing && existing.sales_rep_close));

    writeCookie(cfg.cookieName, JSON.stringify(merged), cfg.cookieMaxAgeDays);
    log(
      cfg,
      "cookie updated",
      merged,
      affiliateChanged ? "(affiliate changed)" : "",
      salesRepChanged ? "(sales rep changed)" : "",
    );
    return { merged: merged, affiliateChanged: affiliateChanged, salesRepChanged: salesRepChanged };
  }

  function getAttribution() {
    var cfg = getConfig();
    return parseCookieAttribution(cfg);
  }

  function syncCartAttributes(attribution, cfg) {
    cfg = cfg || getConfig();
    var attrs = buildOrderAttributes(attribution || getAttribution());
    if (!attrs) return Promise.resolve(null);

    log(cfg, "syncing cart attributes", attrs);

    function attempt(remaining) {
      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ attributes: attrs }),
        __ktCrmOwn: true,
      })
        .then(function (res) {
          if (!res.ok) throw new Error("cart update failed: " + res.status);
          return res.json();
        })
        .then(function (data) {
          log(cfg, "cart synced");
          return data;
        })
        .catch(function (err) {
          if (remaining <= 1) {
            log(cfg, "cart sync error", err);
            return null;
          }
          return new Promise(function (resolve) {
            setTimeout(resolve, 400);
          }).then(function () {
            return attempt(remaining - 1);
          });
        });
    }

    return attempt(3);
  }

  function getProductFormFromCheckoutHost(host) {
    if (!host) return null;
    if (host.closest) {
      var nested = host.closest('form[action*="/cart/add"]');
      if (nested) return nested;
    }
    if (!global.document) return null;
    return global.document.querySelector('form[action*="/cart/add"]');
  }

  function resolveSellingPlan(form) {
    if (form) {
      try {
        var fd = new FormData(form);
        var fromForm = trim(fd.get("selling_plan"));
        if (fromForm) return Number(fromForm) || fromForm;
      } catch (_e) {
        /* ignore */
      }
    }
    if (global.location && global.location.search) {
      var params = parseUrlParams(global.location.search);
      var fromUrl = trim(params.selling_plan);
      if (fromUrl) return Number(fromUrl) || fromUrl;
    }
    return null;
  }

  function checkoutLabelForContext(cfg, context) {
    if (cfg.checkoutButtonLabel) return cfg.checkoutButtonLabel;
    return context === "cart" ? "Check out" : "Buy it now";
  }

  function isElementVisible(el) {
    if (!el || !el.isConnected) return false;
    if (!global.getComputedStyle) return true;
    var style = global.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (parseFloat(style.opacity) === 0) return false;
    return el.offsetParent !== null || style.position === "fixed";
  }

  function hasVisibleReplacement() {
    if (!global.document) return false;
    var btns = global.document.querySelectorAll(REPLACEMENT_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      if (isElementVisible(btns[i])) return true;
    }
    return false;
  }

  function countVisibleReplacements() {
    if (!global.document) return 0;
    var btns = global.document.querySelectorAll(REPLACEMENT_SELECTOR);
    var count = 0;
    for (var i = 0; i < btns.length; i++) {
      if (isElementVisible(btns[i])) count++;
    }
    return count;
  }

  function buildCartAddPayload(form, opts) {
    opts = opts || {};
    var host = opts.host || null;
    var cfg = getConfig();
    var variantId = resolveVariantId(form, host, cfg);
    if (!variantId) return null;

    var quantity = 1;
    if (form) {
      try {
        var fd = new FormData(form);
        quantity = parseInt(String(fd.get("quantity") || "1"), 10);
      } catch (_e) {
        quantity = 1;
      }
    }
    if (!quantity || quantity < 1) quantity = 1;

    var item = { id: Number(variantId), quantity: quantity };
    var sellingPlan = resolveSellingPlan(form);
    if (sellingPlan) item.selling_plan = sellingPlan;
    var attrs = buildOrderAttributes(getAttribution());
    var payload = { items: [item] };

    if (attrs) payload.attributes = attrs;

    var returnTo = opts.forceCheckout ? "/checkout" : null;
    if (form) {
      try {
        var fd2 = new FormData(form);
        var rt = fd2.get("return_to");
        if (rt) returnTo = String(rt);
        if (!returnTo && !opts.forceCheckout) {
          var submitter =
            (form.ownerDocument && form.ownerDocument.activeElement) ||
            form.querySelector('[name="checkout"]');
          var isCheckout =
            submitter &&
            (submitter.name === "checkout" ||
              submitter.getAttribute("name") === "checkout" ||
              submitter.classList.contains("shopify-payment-button__button"));
          if (isCheckout || (rt && String(rt).indexOf("checkout") !== -1)) returnTo = "/checkout";
        }
      } catch (_e2) {
        /* ignore */
      }
    }
    if (opts.forceCheckout) returnTo = "/checkout";

    return {
      payload: payload,
      returnTo: returnTo,
    };
  }

  function parseProductAddForm(form) {
    return buildCartAddPayload(form, { forceCheckout: false });
  }

  function cartFetch(url, init, cfg) {
    init = init || {};
    init.credentials = init.credentials || "same-origin";
    init.__ktCrmOwn = true;
    return fetch(url, init).then(function (res) {
      return res;
    });
  }

  function clearCart(cfg) {
    log(cfg, "cart/clear.js (buy-now reset)");
    return cartFetch(
      "/cart/clear.js",
      { method: "POST", headers: { Accept: "application/json" } },
      cfg,
    ).then(function (res) {
      log(cfg, "cart/clear status:", res.status);
      if (!res.ok) throw new Error("cart clear failed: " + res.status);
      return res.json();
    });
  }

  function postCartAdd(payload, cfg) {
    log(cfg, "cart/add payload:", payload);
    return cartFetch(
      "/cart/add.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      },
      cfg,
    ).then(function (res) {
      log(cfg, "cart/add status:", res.status);
      if (!res.ok) throw new Error("cart add failed: " + res.status);
      return res.json();
    });
  }

  function acceleratedCheckoutWithAttribution(form, host, cfg) {
    cfg = cfg || getConfig();
    var parsed = buildCartAddPayload(form, { forceCheckout: true, host: host });
    if (!parsed) return Promise.reject(new Error("buildCartAddPayload failed"));

    setDebugStep(cfg, "cart-add-start");
    log(cfg, "accelerated intercept → standard checkout", parsed.payload);

    return clearCart(cfg)
      .then(function () {
        setDebugStep(cfg, "cart-cleared");
        return syncCartAttributes(null, cfg);
      })
      .then(function () {
        setDebugStep(cfg, "cart-sync-ok");
        log(cfg, "cart sync ok (pre-add)");
        return postCartAdd(parsed.payload, cfg);
      })
      .then(function (data) {
        setDebugStep(cfg, "redirect-checkout");
        log(cfg, "accelerated cart/add ok — redirecting after status 200");
        log(cfg, "redirect → /checkout");
        global.location.href = parsed.returnTo || "/checkout";
        return data;
      });
  }

  function addToCartWithAttribution(form, cfg) {
    var parsed = parseProductAddForm(form);
    if (!parsed) return Promise.reject(new Error("not a product add form"));

    return postCartAdd(parsed.payload, cfg).then(function (data) {
      log(cfg, "cart/add ok");
      if (parsed.returnTo) global.location.href = parsed.returnTo;
      else global.location.reload();
      return data;
    });
  }

  function isCartApiUrl(url) {
    var s = typeof url === "string" ? url : url && url.url ? String(url.url) : "";
    return /\/cart\/(add|update)\.js/.test(s);
  }

  function mergeAttributesIntoJsonBody(body, attrs) {
    if (!body || !attrs) return body;
    try {
      var parsed = typeof body === "string" ? JSON.parse(body) : body;
      if (!parsed || typeof parsed !== "object") return body;
      parsed.attributes = Object.assign({}, parsed.attributes || {}, attrs);
      return JSON.stringify(parsed);
    } catch (_e) {
      return body;
    }
  }

  function mergeAttributesIntoFormData(body, attrs) {
    if (!body || typeof body.append !== "function" || !attrs) return body;
    for (var key in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, key)) {
        body.set("attributes[" + key + "]", attrs[key]);
      }
    }
    return body;
  }

  function mergeAttributesIntoRequestBody(body, attrs) {
    if (!attrs) return body;
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      return mergeAttributesIntoFormData(body, attrs);
    }
    if (typeof body === "string" && body.charAt(0) === "{") {
      return mergeAttributesIntoJsonBody(body, attrs);
    }
    return body;
  }

  function patchFetchForAttribution() {
    if (global.__ktCrmFetchPatched || !global.fetch) return;
    global.__ktCrmFetchPatched = true;
    var origFetch = global.fetch.bind(global);

    global.fetch = function (input, init) {
      init = init || {};
      var url = typeof input === "string" ? input : input && input.url ? String(input.url) : "";
      if (isCartApiUrl(url) && hasAttributionData(getAttribution()) && !init.__ktCrmOwn) {
        var attrs = buildOrderAttributes(getAttribution());
        if (attrs) {
          var cfg = getConfig();
          log(cfg, "fetch patch merged attrs on", url);
          if (init.body) {
            init.body = mergeAttributesIntoRequestBody(init.body, attrs);
          } else if (url.indexOf("/cart/update") !== -1) {
            init.method = init.method || "POST";
            init.headers = Object.assign(
              { "Content-Type": "application/json", Accept: "application/json" },
              init.headers || {},
            );
            init.body = JSON.stringify({ attributes: attrs });
          }
        }
      }
      return origFetch(input, init);
    };
  }

  var cartSyncTimer = null;
  function scheduleCartSync() {
    if (cartSyncTimer) clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(function () {
      cartSyncTimer = null;
      syncCartAttributes();
    }, CART_SYNC_DEBOUNCE_MS);
  }

  function isProductPage() {
    return global.location && /\/products\//.test(global.location.pathname);
  }

  function isCartPage() {
    return global.location && /\/cart\/?$/.test(global.location.pathname);
  }

  function isCartFormElement(el) {
    if (!el || !el.closest) return false;
    var form = el.closest('form[action*="/cart"]');
    if (!form || !form.action) return false;
    var path = String(form.action).replace(/^https?:\/\/[^/]+/, "");
    path = path.split("?")[0].split("#")[0];
    return /\/cart\/?$/.test(path);
  }

  function isCartContext(el) {
    if (isCartPage()) return true;
    if (!el || !el.closest) return false;
    if (el.closest(CART_DRAWER_SELECTORS)) return true;
    return isCartFormElement(el);
  }

  function shouldUseCartCheckoutFlow(form, host) {
    if (isProductPage() && !isCartContext(host)) return false;
    if (isCartPage() || isCartContext(host)) return true;
    return !buildCartAddPayload(form, { forceCheckout: true, host: host });
  }

  function runCartCheckoutWithAttribution(cfg) {
    cfg = cfg || getConfig();
    setDebugStep(cfg, "cart-checkout");
    log(cfg, "cart checkout via sync → /checkout");
    return syncCartAttributes(null, cfg).then(function () {
      setDebugStep(cfg, "redirect-checkout");
      log(cfg, "redirect → /checkout");
      global.location.href = "/checkout";
    });
  }

  function runAttributedCheckout(form, host, cfg, source, replacementBtn) {
    if (interceptGuard.inFlight) return;
    interceptGuard.inFlight = true;
    interceptGuard.until = Date.now() + INTERCEPT_DEDUPE_MS;
    captureFromUrl();
    setDebugStep(cfg, source + "-checkout");
    log(cfg, "checkout via", source, shouldUseCartCheckoutFlow(form, host) ? "(cart flow)" : "(product flow)");

    if (replacementBtn) {
      setReplacementButtonLoading(
        replacementBtn,
        true,
        host ? findNativePaymentButton(host) : null,
      );
    }

    var checkoutPromise = shouldUseCartCheckoutFlow(form, host)
      ? runCartCheckoutWithAttribution(cfg)
      : acceleratedCheckoutWithAttribution(form, host, cfg);

    checkoutPromise
      .catch(function (err) {
        debugState.lastError = err && err.message ? err.message : String(err);
        setDebugStep(cfg, "checkout-failed", { lastError: debugState.lastError });
        log(cfg, "checkout failed, fallback → /checkout", err);
        if (replacementBtn) setReplacementButtonLoading(replacementBtn, false);
        global.location.href = "/checkout";
      })
      .finally(function () {
        interceptGuard.inFlight = false;
      });
  }

  function getCheckoutMountPoint(host) {
    if (!host) return null;
    return (
      host.closest('[data-shopify="payment-button"]') ||
      host.closest(".shopify-payment-button") ||
      host.closest(".accelerated-checkout-block") ||
      host.parentNode
    );
  }

  function isBlockedCheckoutSource(el) {
    if (!el) return true;
    if (el.getAttribute("data-kt-crm-replacement") === "1") return true;
    if (el.classList.contains("kt-crm-accel-blocked")) return true;
    if (el.getAttribute("aria-hidden") === "true") return true;
    return false;
  }

  function findNativePaymentButton(host) {
    var root = host || global.document;
    if (!root || !root.querySelectorAll) return null;
    var selectors =
      ".shopify-payment-button__button, shopify-buy-it-now-button button, [slot='button'], " +
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]';
    var candidates = root.querySelectorAll(selectors);
    for (var i = 0; i < candidates.length; i++) {
      if (!isBlockedCheckoutSource(candidates[i])) return candidates[i];
    }
    return null;
  }

  function findCartCheckoutButton() {
    if (!global.document || !global.document.querySelectorAll) return null;
    var roots = [];
    var drawer = global.document.querySelector(CART_DRAWER_SELECTORS);
    if (drawer) roots.push(drawer);
    if (isCartPage()) roots.push(global.document);
    for (var r = 0; r < roots.length; r++) {
      var candidates = roots[r].querySelectorAll('button[name="checkout"], input[name="checkout"]');
      for (var i = 0; i < candidates.length; i++) {
        if (!isBlockedCheckoutSource(candidates[i])) return candidates[i];
      }
    }
    return null;
  }

  function collectOverrideHosts() {
    if (!global.document) return [];
    var seen = [];
    var out = [];

    function add(el) {
      if (!el || seen.indexOf(el) !== -1) return;
      seen.push(el);
      out.push(el);
    }

    var accelerated = global.document.querySelectorAll(ACCELERATED_HOST_TAG);
    for (var i = 0; i < accelerated.length; i++) add(accelerated[i]);

    var paymentMounts = global.document.querySelectorAll(
      '[data-shopify="payment-button"], .shopify-payment-button',
    );
    for (var j = 0; j < paymentMounts.length; j++) {
      var pm = paymentMounts[j];
      if (pm.querySelector(ACCELERATED_HOST_TAG)) continue;
      if (pm.querySelector('[data-kt-crm-replacement="1"]')) continue;
      add(pm);
    }

    return out;
  }

  function hideStandaloneWallets(cfg) {
    if (!global.document || !hasAttributionData(getAttribution())) return;
    var nodes = global.document.querySelectorAll(WALLET_SELECTORS);
    var hidden = 0;
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.closest(".kt-crm-checkout-mount") || node.closest('[data-kt-crm-replacement="1"]')) {
        continue;
      }
      if (node.classList.contains("kt-crm-accel-blocked")) continue;
      hideNativeCheckoutHost(node);
      hidden++;
    }
    if (hidden) log(cfg, "standalone wallets hidden:", hidden);
  }

  function scheduleAcceleratedCheckoutOverride(cfg) {
    if (overrideApplyTimer) clearTimeout(overrideApplyTimer);
    overrideApplyTimer = setTimeout(function () {
      overrideApplyTimer = null;
      applyAcceleratedCheckoutOverride(cfg || getConfig());
    }, 150);
  }

  var BUTTON_MIRROR_LAYOUT_PROPS = [
    "align-items",
    "align-self",
    "appearance",
    "border-bottom-left-radius",
    "border-bottom-right-radius",
    "border-radius",
    "border-top-left-radius",
    "border-top-right-radius",
    "box-sizing",
    "cursor",
    "display",
    "flex",
    "flex-basis",
    "flex-grow",
    "flex-shrink",
    "font",
    "font-family",
    "font-size",
    "font-style",
    "font-weight",
    "gap",
    "grid-area",
    "grid-column",
    "grid-column-end",
    "grid-column-start",
    "grid-row",
    "grid-row-end",
    "grid-row-start",
    "height",
    "justify-content",
    "justify-self",
    "letter-spacing",
    "line-height",
    "margin",
    "margin-bottom",
    "margin-left",
    "margin-right",
    "margin-top",
    "max-height",
    "max-width",
    "min-height",
    "min-width",
    "order",
    "padding",
    "padding-bottom",
    "padding-left",
    "padding-right",
    "padding-top",
    "place-self",
    "text-align",
    "text-decoration",
    "text-transform",
    "transition",
    "transition-duration",
    "transition-property",
    "transition-timing-function",
    "vertical-align",
    "width",
    "-webkit-align-self",
    "-webkit-appearance",
    "-webkit-font-smoothing",
    "-webkit-justify-content",
  ];

  var BUTTON_LOADING_CLASSES = [
    "loading",
    "is-loading",
    "button--loading",
    "shopify-payment-button__button--loading",
    "shopify-payment-button__button--disabled",
  ];

  var BUTTON_MIRROR_BASE_PROPS = [
    "background",
    "background-color",
    "background-image",
    "border",
    "border-color",
    "border-style",
    "border-width",
    "box-shadow",
    "color",
    "opacity",
    "outline",
    "outline-offset",
  ];

  var mirrorStyleSeq = 0;

  function walkElementAndShadowAncestors(el) {
    var chain = [];
    while (el) {
      chain.push(el);
      if (el.parentElement) el = el.parentElement;
      else {
        var root = el.getRootNode && el.getRootNode();
        el = root && root.host ? root.host : null;
      }
    }
    return chain;
  }

  function copyComputedStyles(source, target, props) {
    if (!source || !target || !global.getComputedStyle) return;
    if (isBlockedCheckoutSource(source)) return;
    var cs = global.getComputedStyle(source);
    for (var i = 0; i < props.length; i++) {
      var prop = props[i];
      var val = cs.getPropertyValue(prop);
      if (val) target.style.setProperty(prop, val);
    }
  }

  function copyCssVariablesFromAncestors(source, target) {
    if (!source || !target || !global.getComputedStyle) return;
    var chain = walkElementAndShadowAncestors(source);
    for (var i = 0; i < chain.length; i++) {
      var cs = global.getComputedStyle(chain[i]);
      for (var j = 0; j < cs.length; j++) {
        var name = cs[j];
        if (name.indexOf("--") === 0) {
          target.style.setProperty(name, cs.getPropertyValue(name));
        }
      }
    }
  }

  function splitCssSelectors(selectorText) {
    var parts = [];
    var current = "";
    var depth = 0;
    for (var i = 0; i < selectorText.length; i++) {
      var ch = selectorText[i];
      if (ch === "(") depth++;
      if (ch === ")") depth--;
      if (ch === "," && depth === 0) {
        if (current.trim()) parts.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    if (current.trim()) parts.push(current.trim());
    return parts;
  }

  function splitSelectorSuffix(selector) {
    var pseudo = "";
    var sel = selector.trim();
    while (true) {
      var attr = sel.match(/\[(?:[^\]]|\\[\s\S])*\]$/);
      if (attr) {
        pseudo = attr[0] + pseudo;
        sel = sel.slice(0, -attr[0].length);
        continue;
      }
      var ps = sel.match(/::?[a-zA-Z0-9_-]+(?:\([^)]*\))?$/);
      if (ps) {
        pseudo = ps[0] + pseudo;
        sel = sel.slice(0, -ps[0].length);
        continue;
      }
      break;
    }
    return { base: sel.trim(), suffix: pseudo };
  }

  function rewriteSelectorForReplacement(selector, element) {
    var parts = splitSelectorSuffix(selector);
    try {
      if (!parts.base || !element.matches(parts.base)) return null;
    } catch (e) {
      return null;
    }
    var spaceIdx = parts.base.lastIndexOf(" ");
    var ancestor = spaceIdx === -1 ? "" : parts.base.slice(0, spaceIdx);
    return ancestor
      ? ancestor + " " + REPLACEMENT_SELECTOR + parts.suffix
      : REPLACEMENT_SELECTOR + parts.suffix;
  }

  function walkStylesheetRules(rules, element, out, mediaQuery) {
    if (!rules) return;
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (rule.selectorText && rule.style) {
        var selectors = splitCssSelectors(rule.selectorText);
        for (var s = 0; s < selectors.length; s++) {
          var rewritten = rewriteSelectorForReplacement(selectors[s], element);
          if (!rewritten) continue;
          out.push({
            media: mediaQuery || "",
            selector: rewritten,
            cssText: rule.style.cssText,
          });
          if (/\[disabled\]|:disabled/.test(selectors[s])) {
            out.push({
              media: mediaQuery || "",
              selector: rewritten.replace(
                REPLACEMENT_SELECTOR,
                REPLACEMENT_SELECTOR + '[aria-busy="true"]',
              ),
              cssText: rule.style.cssText,
            });
          }
          if (/:active\b/.test(selectors[s])) {
            out.push({
              media: mediaQuery || "",
              selector: rewritten.replace(/:active\b/g, ".kt-crm-btn-pressed"),
              cssText: rule.style.cssText,
            });
          }
        }
      } else if (rule.cssRules) {
        var nestedMedia = mediaQuery || "";
        if (rule.type === 4 || rule.conditionText) nestedMedia = rule.conditionText || nestedMedia;
        walkStylesheetRules(rule.cssRules, element, out, nestedMedia);
      }
    }
  }

  function collectRulesForElement(element) {
    var out = [];
    if (!global.document || !global.document.styleSheets) return out;
    var sheets = global.document.styleSheets;
    for (var i = 0; i < sheets.length; i++) {
      try {
        walkStylesheetRules(sheets[i].cssRules, element, out, "");
      } catch (e) {
        // cross-origin stylesheet
      }
    }
    return out;
  }

  function extractAnimationNames(cssText) {
    var names = [];
    var re = /(?:animation(?:-name)?\s*:\s*([^;]+))/gi;
    var match;
    while ((match = re.exec(cssText))) {
      var chunk = match[1].split(",");
      for (var i = 0; i < chunk.length; i++) {
        var name = trim(chunk[i].replace(/!important/gi, ""));
        if (name && name !== "none" && name !== "inherit" && names.indexOf(name) === -1) {
          names.push(name);
        }
      }
    }
    return names;
  }

  function collectKeyframeRules(names) {
    var blocks = [];
    if (!names.length || !global.document || !global.document.styleSheets) return blocks;
    var wanted = {};
    for (var n = 0; n < names.length; n++) wanted[names[n]] = true;

    function walkKeyframes(rules, mediaQuery) {
      if (!rules) return;
      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];
        if (rule.type === 7 && rule.name && wanted[rule.name]) {
          var block = rule.cssText;
          blocks.push(mediaQuery ? "@media " + mediaQuery + "{" + block + "}" : block);
        } else if (rule.cssRules) {
          var nestedMedia = mediaQuery || "";
          if (rule.type === 4 || rule.conditionText) nestedMedia = rule.conditionText || nestedMedia;
          walkKeyframes(rule.cssRules, nestedMedia);
        }
      }
    }

    var sheets = global.document.styleSheets;
    for (var s = 0; s < sheets.length; s++) {
      try {
        walkKeyframes(sheets[s].cssRules, "");
      } catch (e) {}
    }
    return blocks;
  }

  function buildComputedBaseRule(source) {
    if (!source || !global.getComputedStyle) return "";
    var cs = global.getComputedStyle(source);
    var parts = [];
    for (var i = 0; i < BUTTON_MIRROR_BASE_PROPS.length; i++) {
      var prop = BUTTON_MIRROR_BASE_PROPS[i];
      var val = cs.getPropertyValue(prop);
      if (val) parts.push(prop + ":" + val);
    }
    if (!parts.length) return "";
    return REPLACEMENT_SELECTOR + "{" + parts.join(";") + "}";
  }

  function buildDisabledBusyRule(nativeBtn) {
    if (!nativeBtn || !global.getComputedStyle) return "";
    var prev = nativeBtn.disabled;
    nativeBtn.disabled = true;
    var cs = global.getComputedStyle(nativeBtn);
    var props = BUTTON_MIRROR_BASE_PROPS.concat(["opacity", "cursor", "pointer-events"]);
    var parts = [];
    for (var i = 0; i < props.length; i++) {
      var val = cs.getPropertyValue(props[i]);
      if (val) parts.push(props[i] + ":" + val);
    }
    nativeBtn.disabled = prev;
    if (!parts.length) parts.push("opacity:0.65", "cursor:wait");
    return REPLACEMENT_SELECTOR + '[aria-busy="true"]{' + parts.join(";") + "}";
  }

  function injectMirroredButtonRules(nativeBtn, replacementBtn) {
    if (!nativeBtn || !replacementBtn || !global.document) return;
    var mount = replacementBtn.closest(".kt-crm-checkout-mount");
    if (!mount) return;

    if (!mount.dataset.ktCrmMirrorId) {
      mirrorStyleSeq += 1;
      mount.dataset.ktCrmMirrorId = String(mirrorStyleSeq);
    }
    var styleId = "kt-crm-btn-mirror-" + mount.dataset.ktCrmMirrorId;
    var existing = global.document.getElementById(styleId);
    if (existing) existing.remove();

    var rules = collectRulesForElement(nativeBtn);
    if (!rules.length) return;

    var cssBlocks = [];
    var baseRule = buildComputedBaseRule(nativeBtn);
    if (baseRule) cssBlocks.push(baseRule);
    var busyRule = buildDisabledBusyRule(nativeBtn);
    if (busyRule) cssBlocks.push(busyRule);

    var animationNames = [];
    var grouped = {};
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      var key = r.media || "";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(r.selector + "{" + r.cssText + "}");
      var names = extractAnimationNames(r.cssText);
      for (var n = 0; n < names.length; n++) {
        if (animationNames.indexOf(names[n]) === -1) animationNames.push(names[n]);
      }
    }
    for (var media in grouped) {
      if (!Object.prototype.hasOwnProperty.call(grouped, media)) continue;
      var block = grouped[media].join("");
      cssBlocks.push(media ? "@media " + media + "{" + block + "}" : block);
    }
    cssBlocks = cssBlocks.concat(collectKeyframeRules(animationNames));

    var style = global.document.createElement("style");
    style.id = styleId;
    style.setAttribute("data-kt-crm-button-mirror", "1");
    style.textContent = cssBlocks.join("");
    global.document.head.appendChild(style);
  }

  function toggleShopifyButtonLoadingUi(btn, loading) {
    if (!btn) return;
    var spinner = btn.querySelector(".shopify-payment-button__spinner, [class*='spinner']");
    if (!spinner) return;
    var label = btn.querySelector(".shopify-payment-button__button-text");
    spinner.hidden = !loading;
    spinner.style.display = loading ? "" : "none";
    if (label) label.style.visibility = loading ? "hidden" : "";
  }

  function bindReplacementButtonPress(btn) {
    if (!btn || btn.dataset.ktCrmPressBound) return;
    btn.dataset.ktCrmPressBound = "1";
    btn.addEventListener("pointerdown", function () {
      btn.classList.add("kt-crm-btn-pressed");
    });
    function release() {
      btn.classList.remove("kt-crm-btn-pressed");
    }
    btn.addEventListener("pointerup", release);
    btn.addEventListener("pointerleave", release);
    btn.addEventListener("pointercancel", release);
  }

  function setReplacementButtonLoading(btn, loading, nativeBtn) {
    if (!btn) return;
    if (loading) {
      if (!btn.dataset.ktCrmOriginalText) btn.dataset.ktCrmOriginalText = btn.textContent;
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      btn.classList.add("kt-crm-checkout-loading");
      for (var i = 0; i < BUTTON_LOADING_CLASSES.length; i++) {
        btn.classList.add(BUTTON_LOADING_CLASSES[i]);
      }
      toggleShopifyButtonLoadingUi(btn, true);
      return;
    }
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    btn.classList.remove("kt-crm-btn-pressed", "kt-crm-checkout-loading");
    for (var k = 0; k < BUTTON_LOADING_CLASSES.length; k++) {
      btn.classList.remove(BUTTON_LOADING_CLASSES[k]);
    }
    toggleShopifyButtonLoadingUi(btn, false);
    if (btn.dataset.ktCrmOriginalText) btn.textContent = btn.dataset.ktCrmOriginalText;
  }

  function resolveButtonMount(source, mountHint) {
    if (mountHint) return mountHint;
    if (!source || !source.closest) return source && source.parentElement ? source.parentElement : null;
    return (
      source.closest(DAWN_PRODUCT_BUTTONS) ||
      source.closest(".kt-crm-checkout-mount") ||
      source.parentElement
    );
  }

  function applyStretchWidthFromSource(source, target, mount) {
    if (!source || !target || !global.getComputedStyle) return;
    var cs = global.getComputedStyle(source);
    var alignSelf = trim(cs.getPropertyValue("align-self"));
    if (alignSelf && alignSelf !== "auto") target.style.setProperty("align-self", alignSelf);

    var mountEl = resolveButtonMount(source, mount);
    if (mountEl && mountEl.clientWidth > 0 && source.offsetWidth > 0) {
      if (source.offsetWidth / mountEl.clientWidth >= 0.85) {
        target.style.setProperty("width", "100%", "important");
        target.style.setProperty("max-width", "100%", "important");
        if (!alignSelf || alignSelf === "auto") {
          target.style.setProperty("align-self", "stretch", "important");
        }
        target.style.setProperty("box-sizing", "border-box", "important");
        return;
      }
    }

    if (trim(cs.getPropertyValue("width")) === "100%") {
      target.style.setProperty("width", "100%", "important");
      target.style.setProperty("max-width", "100%", "important");
    }
  }

  function applyMountLayoutMatch(mount, target) {
    if (!mount || !target || !global.getComputedStyle) return;
    var mcs = global.getComputedStyle(mount);
    var display = mcs.getPropertyValue("display");
    var flexDir = mcs.getPropertyValue("flex-direction");
    if (display === "flex" && (flexDir === "column" || flexDir === "column-reverse")) {
      target.style.setProperty("width", "100%", "important");
      target.style.setProperty("max-width", "100%", "important");
      target.style.setProperty("align-self", "stretch", "important");
      target.style.setProperty("flex-shrink", "0", "important");
    }
    if (display === "grid") {
      target.style.setProperty("width", "100%", "important");
      var justifyItems = trim(mcs.getPropertyValue("justify-items"));
      target.style.setProperty(
        "justify-self",
        justifyItems && justifyItems !== "normal" ? justifyItems : "stretch",
        "important",
      );
    }
  }

  function mirrorButtonAppearance(source, target, mountHint) {
    if (!source || !target) return;
    var mount = resolveButtonMount(source, mountHint);
    copyCssVariablesFromAncestors(source, target);
    copyComputedStyles(source, target, BUTTON_MIRROR_LAYOUT_PROPS);
    var sourceNodes = source.querySelectorAll("*");
    var targetNodes = target.querySelectorAll("*");
    var limit = Math.min(sourceNodes.length, targetNodes.length);
    for (var i = 0; i < limit; i++) {
      copyComputedStyles(sourceNodes[i], targetNodes[i], BUTTON_MIRROR_LAYOUT_PROPS);
    }
    injectMirroredButtonRules(source, target);
    applyStretchWidthFromSource(source, target, mount);
    if (mount) applyMountLayoutMatch(mount, target);
  }

  function hideNativeCheckoutHost(host) {
    if (!host) return;
    host.classList.add("kt-crm-accel-blocked");
    host.setAttribute("aria-hidden", "true");
    host.style.setProperty("display", "none", "important");
    host.style.setProperty("visibility", "hidden", "important");
    host.style.setProperty("height", "0", "important");
    host.style.setProperty("max-height", "0", "important");
    host.style.setProperty("overflow", "hidden", "important");
    host.style.setProperty("pointer-events", "none", "important");
    host.style.setProperty("margin", "0", "important");
    host.style.setProperty("padding", "0", "important");
  }

  function unhideNativeCheckoutHost(host) {
    if (!host) return;
    host.classList.remove("kt-crm-accel-blocked");
    host.removeAttribute("aria-hidden");
    host.style.removeProperty("display");
    host.style.removeProperty("visibility");
    host.style.removeProperty("height");
    host.style.removeProperty("max-height");
    host.style.removeProperty("overflow");
    host.style.removeProperty("pointer-events");
    host.style.removeProperty("margin");
    host.style.removeProperty("padding");
  }

  function hideAllWalletUi(cfg) {
    if (!global.document || !hasAttributionData(getAttribution())) return;
    var hidden = 0;

    var walletBlocks = global.document.querySelectorAll(WALLET_UI_SELECTORS);
    for (var w = 0; w < walletBlocks.length; w++) {
      var block = walletBlocks[w];
      if (block.matches && block.matches(REPLACEMENT_SELECTOR)) continue;
      if (block.classList.contains("kt-crm-accel-blocked")) continue;
      hideNativeCheckoutHost(block);
      hidden++;
    }

    var hosts = collectOverrideHosts();
    for (var h = 0; h < hosts.length; h++) {
      if (hosts[h].classList.contains("kt-crm-accel-blocked")) continue;
      hideNativeCheckoutHost(hosts[h]);
      hidden++;
    }

    hideStandaloneWallets(cfg);
    if (hidden) log(cfg, "wallet UI hidden:", hidden);
  }

  function createInjectedCheckoutButton(styleSource, label, onClick) {
    var btn = global.document.createElement("button");
    btn.type = "button";
    btn.setAttribute("data-kt-crm-replacement", "1");
    btn.className = (styleSource && styleSource.className) || "button product-form__submit";
    btn.textContent = label;
    btn.disabled = false;
    btn.removeAttribute("aria-hidden");
    bindReplacementButtonPress(btn);
    btn.addEventListener("click", function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      onClick(ev, btn);
    });
    return btn;
  }

  function injectCheckoutButtonOnProduct(cfg) {
    if (!global.document || !isProductPage()) return null;
    cfg = cfg || getConfig();
    var mounts = global.document.querySelectorAll(DAWN_PRODUCT_BUTTONS);
    var injected = null;
    var label = checkoutLabelForContext(cfg, "product");

    for (var i = 0; i < mounts.length; i++) {
      var mount = mounts[i];
      var existing = mount.querySelector(REPLACEMENT_SELECTOR);
      if (existing) {
        if (isElementVisible(existing)) injected = existing;
        continue;
      }

      var addBtn = mount.querySelector(DAWN_ADD_TO_CART);
      if (!addBtn) {
        log(cfg, "inject: no add-to-cart button in mount, skip");
        continue;
      }

      var form =
        mount.closest('form[action*="/cart/add"]') ||
        (global.document.querySelector("product-form form") || null);
      if (!form) {
        log(cfg, "inject: no product form found, skip");
        continue;
      }

      var btn = createInjectedCheckoutButton(addBtn, label, function (_ev, replacementBtn) {
        runAttributedCheckout(form, null, cfg, "injected-button", replacementBtn);
      });

      var walletEl = mount.querySelector(WALLET_UI_SELECTORS);
      if (walletEl) mount.insertBefore(btn, walletEl);
      else if (addBtn.nextSibling) mount.insertBefore(btn, addBtn.nextSibling);
      else mount.appendChild(btn);

      mount.classList.add("kt-crm-checkout-mount");
      mirrorButtonAppearance(addBtn, btn, mount);
      if (global.requestAnimationFrame) {
        global.requestAnimationFrame(function () {
          mirrorButtonAppearance(addBtn, btn, mount);
        });
      }

      injected = btn;
      log(cfg, "injected buy-it-now on product page", label);
    }

    return injected;
  }

  function injectCheckoutButtonOnCart(cfg) {
    if (!global.document) return null;
    cfg = cfg || getConfig();
    var label = checkoutLabelForContext(cfg, "cart");
    var injected = null;
    var contexts = [];

    if (isCartPage()) {
      var cartFooter = global.document.querySelector(".cart__footer, .cart__ctas");
      if (cartFooter) contexts.push({ mount: cartFooter, styleSel: ".cart__checkout-button, .button" });
    }

    var drawer = global.document.querySelector(CART_DRAWER_SELECTORS);
    if (drawer) {
      var drawerFooter = drawer.querySelector(".drawer__footer, .cart-drawer__footer");
      if (drawerFooter) contexts.push({ mount: drawerFooter, styleSel: ".cart__checkout-button, .button" });
    }

    for (var c = 0; c < contexts.length; c++) {
      var ctx = contexts[c];
      var mount = ctx.mount;
      var existing = mount.querySelector(REPLACEMENT_SELECTOR);
      if (existing) {
        if (isElementVisible(existing)) injected = existing;
        continue;
      }

      var styleSource = mount.querySelector(ctx.styleSel);
      var nativeCheckout = mount.querySelector('button[name="checkout"], input[name="checkout"]');
      if (!styleSource && nativeCheckout) styleSource = nativeCheckout;

      var btn = createInjectedCheckoutButton(styleSource, label, function (_ev, replacementBtn) {
        runCartCheckoutWithAttribution(cfg);
        setReplacementButtonLoading(replacementBtn, true);
      });

      if (nativeCheckout && nativeCheckout.parentNode === mount) {
        mount.insertBefore(btn, nativeCheckout);
      } else {
        mount.appendChild(btn);
      }

      if (!isElementVisible(btn)) continue;

      if (nativeCheckout && nativeCheckout.parentNode === mount) {
        hideNativeCheckoutHost(nativeCheckout);
      }

      mount.classList.add("kt-crm-checkout-mount");
      if (styleSource) {
        mirrorButtonAppearance(styleSource, btn, mount);
        if (global.requestAnimationFrame) {
          global.requestAnimationFrame(function () {
            mirrorButtonAppearance(styleSource, btn, mount);
          });
        }
      }

      injected = btn;
      log(cfg, "injected checkout on cart/drawer", label);
    }

    return injected;
  }

  function ensureReplacementStillMounted(cfg) {
    if (!global.document || !hasAttributionData(getAttribution())) return;
    if (hasVisibleReplacement()) return;
    if (!global.document.querySelector(".kt-crm-accel-blocked")) return;
    log(cfg, "replacement lost while wallets hidden — re-injecting");
    injectCheckoutButtonOnProduct(cfg);
    injectCheckoutButtonOnCart(cfg);
  }

  function hideNativeAccelerated(host) {
    hideNativeCheckoutHost(host);
  }

  function insertCustomCheckoutButton(mount, nativeBtn, host, cfg, source) {
    if (!mount || !nativeBtn) return null;

    var btn = nativeBtn.cloneNode(true);
    btn.setAttribute("data-kt-crm-replacement", "1");
    if (nativeBtn.className) btn.className = nativeBtn.className;
    btn.type = "button";
    btn.disabled = false;
    btn.removeAttribute("aria-hidden");
    if (cfg.checkoutButtonLabel) btn.textContent = cfg.checkoutButtonLabel;

    bindReplacementButtonPress(btn);

    btn.addEventListener("click", function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      var form = host ? getProductFormFromCheckoutHost(host) : null;
      runAttributedCheckout(form, host, cfg, source, btn);
    });

    var insertBefore = null;
    if (host && host.parentNode === mount) {
      insertBefore = host;
    } else if (host === mount) {
      insertBefore = mount.firstChild;
    } else {
      insertBefore = mount.querySelector(ACCELERATED_HOST_TAG) || nativeBtn;
    }
    mount.insertBefore(btn, insertBefore);

    mirrorButtonAppearance(nativeBtn, btn, mount);
    if (global.requestAnimationFrame) {
      global.requestAnimationFrame(function () {
        mirrorButtonAppearance(nativeBtn, btn, mount);
      });
    }

    return btn;
  }

  function mountCustomCheckoutOnCart(cfg) {
    if (!global.document || !hasAttributionData(getAttribution())) return;

    var nativeBtn = findCartCheckoutButton();
    if (!nativeBtn || nativeBtn.getAttribute("data-kt-crm-replacement") === "1") return;

    var mount = nativeBtn.parentNode;
    if (!mount || mount.querySelector('[data-kt-crm-replacement="1"]')) return;

    mount.classList.add("kt-crm-checkout-mount");

    var btn = insertCustomCheckoutButton(mount, nativeBtn, null, cfg, "custom-button-cart");
    if (!btn || !isElementVisible(btn)) return;

    hideNativeCheckoutHost(nativeBtn);
    log(cfg, "replaced cart checkout button", btn.className, btn.textContent.trim());
  }

  function hideChildrenExceptReplacement(mount) {
    if (!mount || !mount.children) return;
    for (var i = 0; i < mount.children.length; i++) {
      var child = mount.children[i];
      if (child.hasAttribute("data-kt-crm-replacement")) continue;
      if (child.classList.contains("kt-crm-accel-blocked")) continue;
      hideNativeCheckoutHost(child);
    }
  }

  function mountCustomCheckoutOnHost(host, cfg) {
    if (!host) return;
    var mount = getCheckoutMountPoint(host);
    if (!mount) return;

    var existingReplacement = mount.querySelector('[data-kt-crm-replacement="1"]');
    if (existingReplacement) {
      if (!isElementVisible(existingReplacement)) return;
      if (host !== mount) hideNativeCheckoutHost(host);
      hideChildrenExceptReplacement(mount);
      return;
    }

    var nativeBtn = findNativePaymentButton(host) || findNativePaymentButton(mount);
    if (!nativeBtn) {
      log(cfg, "native payment button not ready yet, will retry");
      return;
    }

    mount.classList.add("kt-crm-checkout-mount");

    var btn = insertCustomCheckoutButton(mount, nativeBtn, host, cfg, "custom-button");
    if (!btn || !isElementVisible(btn)) return;

    if (host !== mount) hideNativeCheckoutHost(host);
    hideChildrenExceptReplacement(mount);
    log(cfg, "replaced native button (cloned + theme rules)", btn.className, btn.textContent.trim());
  }

  function applyAcceleratedCheckoutOverride(cfg) {
    if (overrideApplyInProgress) return;
    cfg = cfg || getConfig();
    if (!cfg.customCheckoutButton || !hasAttributionData(getAttribution()) || !global.document) return;

    overrideApplyInProgress = true;
    try {
      injectCheckoutButtonOnProduct(cfg);
      injectCheckoutButtonOnCart(cfg);

      if (!hasVisibleReplacement()) {
        var hosts = collectOverrideHosts();
        for (var i = 0; i < hosts.length; i++) mountCustomCheckoutOnHost(hosts[i], cfg);
        mountCustomCheckoutOnCart(cfg);
      }

      ensureReplacementStillMounted(cfg);

      if (hasVisibleReplacement()) {
        hideAllWalletUi(cfg);
        debugState.lastError = null;
        setDebugStep(cfg, "custom-button-ready");
      } else {
        debugState.lastError = "inject pending";
        setDebugStep(cfg, "inject-pending");
        log(cfg, "inject pending — wallets stay visible until button mounts");
      }
    } finally {
      overrideApplyInProgress = false;
    }
  }

  function ensureCustomCheckoutButton(cfg) {
    cfg = cfg || getConfig();
    if (!cfg.customCheckoutButton || !hasAttributionData(getAttribution())) return;

    applyAcceleratedCheckoutOverride(cfg);

    if (overrideRetryTimer) return;
    overrideRetryTimer = setInterval(function () {
      var currentCfg = getConfig();
      if (!currentCfg.customCheckoutButton || !hasAttributionData(getAttribution())) return;
      if (hasVisibleReplacement()) {
        hideAllWalletUi(currentCfg);
        ensureReplacementStillMounted(currentCfg);
        return;
      }
      applyAcceleratedCheckoutOverride(currentCfg);
    }, 500);
  }

  function watchAcceleratedCheckoutOverride(cfg) {
    cfg = cfg || getConfig();
    if (!cfg.customCheckoutButton || !hasAttributionData(getAttribution())) return;

    ensureCustomCheckoutButton(cfg);

    if (overrideObserver || !global.document || !global.document.body) return;
    overrideObserver = new MutationObserver(function () {
      scheduleAcceleratedCheckoutOverride(getConfig());
    });
    overrideObserver.observe(global.document.body, { childList: true, subtree: true });
  }

  function init(options) {
    if (options && typeof options === "object") {
      global.KT_CRM_AFFILIATE = Object.assign({}, global.KT_CRM_AFFILIATE || {}, options);
    }
    var cfg = getConfig();
    setDebugStep(cfg, "init");
    log(cfg, "init", {
      debug: cfg.debug,
      debugOverlay: cfg.debugOverlay,
      interceptAcceleratedCheckout: cfg.interceptAcceleratedCheckout,
      customCheckoutButton: cfg.customCheckoutButton,
      blockNativeAccelerated: cfg.blockNativeAccelerated,
      hasAttribution: hasAttributionData(getAttribution()),
    });
    patchFetchForAttribution();
    var capture = captureFromUrl();
    if (hasAttributionData(getAttribution())) {
      log(cfg, "attribution cookie", getAttribution());
      if (
        (capture && (capture.affiliateChanged || capture.salesRepChanged)) ||
        isProductPage() ||
        isCartPage()
      ) {
        log(
          cfg,
          capture && capture.salesRepChanged
            ? "sales rep changed — immediate cart sync"
            : capture && capture.affiliateChanged
              ? "affiliate changed — immediate cart sync"
              : (isProductPage() ? "product" : "cart") + " page — proactive cart sync",
        );
        syncCartAttributes(null, cfg);
      } else {
        scheduleCartSync();
      }
    }
    scanAcceleratedHosts(cfg);
    var formEl = global.document && global.document.querySelector('form[action*="/cart/add"]');
    var hostEl = global.document && global.document.querySelector(ACCELERATED_HOST_TAG);
    resolveVariantId(formEl, hostEl, cfg);
    updateDebugOverlay(cfg);

    setTimeout(function () {
      scanAcceleratedHosts(cfg);
      resolveVariantId(
        global.document && global.document.querySelector('form[action*="/cart/add"]'),
        global.document && global.document.querySelector(ACCELERATED_HOST_TAG),
        cfg,
      );
      ensureCustomCheckoutButton(cfg);
      updateDebugOverlay(cfg);
    }, 1500);

    ensureCustomCheckoutButton(cfg);

    return getAttribution();
  }

  function runNativeIntercept(ev, eventType) {
    var cfg = getConfig();
    if (cfg.customCheckoutButton && cfg.blockNativeAccelerated) return;
    if (!cfg.interceptAcceleratedCheckout) return;
    if (!hasAttributionData(getAttribution())) return;

    var now = Date.now();
    if (interceptGuard.inFlight || now < interceptGuard.until) return;

    var host = findAcceleratedHostInEvent(ev);
    if (!host) return;

    var tags = pathTags(ev);
    log(cfg, "intercept:", eventType, "target:", ev.target && ev.target.tagName, "composedPath:", tags);

    var form = getProductFormFromCheckoutHost(host);
    if (!form) log(cfg, "intercept: no product form found, trying variant from host only");

    captureFromUrl();
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation();

    debugState.lastIntercept = {
      event: eventType,
      blocked: true,
      composedPath: tags,
      error: null,
    };
    setDebugStep(cfg, "intercept-blocked");
    log(cfg, "native blocked: preventDefault applied");

    runAttributedCheckout(form, host, cfg, "native-intercept");
  }

  function bindAcceleratedCheckoutIntercept() {
    if (!global.document) return;

    global.document.addEventListener(
      "pointerdown",
      function (ev) {
        runNativeIntercept(ev, "pointerdown");
      },
      true,
    );
  }

  function debugReport() {
    var cfg = getConfig();
    var form = global.document && global.document.querySelector('form[action*="/cart/add"]');
    var host = global.document && global.document.querySelector(ACCELERATED_HOST_TAG);
    return {
      step: debugState.step,
      config: {
        debug: cfg.debug,
        debugOverlay: cfg.debugOverlay,
        interceptAcceleratedCheckout: cfg.interceptAcceleratedCheckout,
        customCheckoutButton: cfg.customCheckoutButton,
        blockNativeAccelerated: cfg.blockNativeAccelerated,
      },
      hasAttribution: hasAttributionData(getAttribution()),
      attribution: getAttribution(),
      orderAttributes: buildOrderAttributes(getAttribution()),
      hosts: getAcceleratedHostsSnapshot(),
      variantId: debugState.variantId || resolveVariantId(form, host, cfg),
      variantSource: debugState.variantSource,
      lastIntercept: debugState.lastIntercept,
      lastError: debugState.lastError,
      fetchPatched: Boolean(global.__ktCrmFetchPatched),
      customButtonsMounted: global.document
        ? global.document.querySelectorAll('[data-kt-crm-replacement="1"]').length
        : 0,
      visibleReplacements: countVisibleReplacements(),
      injectPending: !hasVisibleReplacement() && hasAttributionData(getAttribution()),
    };
  }

  function navigateCheckoutAfterSync(ev, cfg, href) {
    if (!hasAttributionData(getAttribution())) return false;
    if (ev.target && ev.target.closest && ev.target.closest('[data-kt-crm-replacement="1"]')) {
      return false;
    }
    if (interceptGuard.inFlight) {
      ev.preventDefault();
      ev.stopPropagation();
      return true;
    }

    ev.preventDefault();
    ev.stopPropagation();
    captureFromUrl();
    interceptGuard.inFlight = true;
    log(cfg, "checkout navigation intercepted — syncing attrs first");

    syncCartAttributes(null, cfg)
      .catch(function (err) {
        log(cfg, "pre-checkout sync error", err);
      })
      .then(function () {
        global.location.href = href || "/checkout";
      })
      .finally(function () {
        interceptGuard.inFlight = false;
      });

    return true;
  }

  function bindCartEvents() {
    if (!global.document) return;

    global.document.addEventListener(
      "submit",
      function (ev) {
        var form = ev.target;
        if (!form || !form.action) return;
        var action = String(form.action);
        var cfg = getConfig();

        if (action.indexOf("/cart/add") !== -1 && hasAttributionData(getAttribution())) {
          captureFromUrl();
          ev.preventDefault();
          ev.stopPropagation();
          addToCartWithAttribution(form, cfg).catch(function (err) {
            log(cfg, "cart/add intercept failed, fallback", err);
            form.submit();
          });
          return;
        }

        if (action.indexOf("/cart/add") !== -1 || action.indexOf("/cart") !== -1) {
          captureFromUrl();
          if (
            hasAttributionData(getAttribution()) &&
            ev.submitter &&
            ev.submitter.name === "checkout"
          ) {
            ev.preventDefault();
            ev.stopPropagation();
            syncCartAttributes(null, cfg).then(function () {
              global.location.href = "/checkout";
            });
            return;
          }
          syncCartAttributes();
        }
      },
      true,
    );

    global.document.addEventListener(
      "click",
      function (ev) {
        var el = ev.target;
        if (!el || !el.closest) return;
        var cfg = getConfig();
        if (!hasAttributionData(getAttribution())) return;

        if (el.closest('[data-kt-crm-replacement="1"]')) return;

        var accelHost = findAcceleratedHostInEvent(ev);
        if (accelHost && cfg.customCheckoutButton) {
          ev.preventDefault();
          ev.stopPropagation();
          var form = getProductFormFromCheckoutHost(accelHost);
          runAttributedCheckout(form, accelHost, cfg, "fallback-intercept");
          return;
        }

        var checkoutLink = el.closest('a[href*="/checkout"]');
        if (checkoutLink) {
          navigateCheckoutAfterSync(ev, cfg, checkoutLink.href);
          return;
        }

        var checkoutBtn = el.closest(
          'button[name="checkout"], input[name="checkout"], [data-checkout-url]',
        );
        if (checkoutBtn) {
          var checkoutUrl =
            checkoutBtn.getAttribute("data-checkout-url") ||
            checkoutBtn.value ||
            "/checkout";
          navigateCheckoutAfterSync(ev, cfg, checkoutUrl);
        }
      },
      true,
    );
  }

  var api = {
    COOKIE_NAME: COOKIE_NAME,
    init: init,
    captureFromUrl: captureFromUrl,
    getAttribution: getAttribution,
    buildOrderAttributes: buildOrderAttributes,
    syncCartAttributes: syncCartAttributes,
    scheduleCartSync: scheduleCartSync,
    bindCartEvents: bindCartEvents,
    bindAcceleratedCheckoutIntercept: bindAcceleratedCheckoutIntercept,
    applyAcceleratedCheckoutOverride: applyAcceleratedCheckoutOverride,
    ensureCustomCheckoutButton: ensureCustomCheckoutButton,
    injectCheckoutButtonOnProduct: injectCheckoutButtonOnProduct,
    injectCheckoutButtonOnCart: injectCheckoutButtonOnCart,
    hasVisibleReplacement: hasVisibleReplacement,
    watchAcceleratedCheckoutOverride: watchAcceleratedCheckoutOverride,
    patchFetchForAttribution: patchFetchForAttribution,
    acceleratedCheckoutWithAttribution: acceleratedCheckoutWithAttribution,
    resolveVariantId: resolveVariantId,
    debugReport: debugReport,
    parseUrlParams: parseUrlParams,
    extractFromParams: extractFromParams,
    mergeAttribution: mergeAttribution,
    normalizeAffiliateRef: normalizeAffiliateRef,
  };

  global.KtCrmAttribution = api;
})(typeof window !== "undefined" ? window : globalThis);
